import SellIcon from '@mui/icons-material/Sell';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Button, Stack, Typography } from '@mui/material';
import type { StatusesByField } from 'business/types';
import routes from 'components/App/components/routes';
import FieldStatusChip from 'components/SharedComponents/FieldStatusInformation/FieldStatusChip';
import TooltipIconButton from 'components/TooltipIconButton';
import { capitalizeAndKeepTheRest, getFieldName } from 'helpers/helpers';
import { useAPIToggleFieldVisibilityMutation } from 'modules/datacatalog/api/useAPIToggleFieldVisibilityMutation';
import { useMappingTarget } from 'modules/datacatalog/hooks/useMappingTarget';
import type {
    CatalogTreeField,
    FieldStatusData,
} from 'modules/datacatalog/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSelector } from 'store/hooks';

import DataTypeIcon from '../../DataTypeIcon/DataTypeIcon';
import Breadcrumb from './Breadcrumb';
import EditFieldStatusModal from './EditFieldStatusModal';

const FieldHeader = ({
    item,
    domain,
    category,
    isFieldVisible,
    mappingCategory = null,
    canEdit,
    fieldStatusData,
    displayedStatuses,
}: {
    item: CatalogTreeField;
    domain: string;
    category: string;
    isFieldVisible: boolean;
    mappingCategory?: string | null;
    canEdit: boolean;
    fieldStatusData: FieldStatusData;
    displayedStatuses?: StatusesByField;
}) => {
    const [openFieldStatusModal, setOpenFieldStatusModal] = useState(false);

    const { t } = useTranslation();
    const isSupportUser = useSelector((state) => state.vision.isSupportUser);
    const isAdminUser = useSelector((state) => state.vision.isAdmin);
    const mappingTarget = useMappingTarget(mappingCategory, item.table);
    const { mutate: toggleFieldVisibility } =
        useAPIToggleFieldVisibilityMutation(item.field);

    const visibilityTooltip = isFieldVisible
        ? t('dataCatalog.markFieldAsHidden')
        : t('dataCatalog.markFieldAsVisible');

    const canUpdateFieldVisibility = isSupportUser || isAdminUser;
    const hiddenClassName = !isFieldVisible ? 'dataCatalog-card-hidden' : '';
    const toggleIsVisible = (): void => {
        toggleFieldVisibility({
            table: item.table,
            name: getFieldName(item.field),
        });
    };

    const isTable = item.type === 'table';

    const statusToolTip = isTable
        ? t('dataCatalog.fieldStatusManagement.tableCTA')
        : t('dataCatalog.fieldStatusManagement.fieldCTA');

    const visibilityButton = isFieldVisible ? (
        <Visibility />
    ) : (
        <VisibilityOff />
    );
    return (
        <>
            <Stack spacing={0.5}>
                <Breadcrumb
                    domain={domain}
                    category={category}
                    table={isTable ? undefined : item.table}
                />
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center' }}
                    >
                        <DataTypeIcon
                            type={item.type}
                            sx={{ fontSize: '1.3rem' }}
                            color="primary"
                        />
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, lineHeight: 1.3 }}
                        >
                            {capitalizeAndKeepTheRest(item.label)}
                        </Typography>
                        {displayedStatuses && (
                            <FieldStatusChip
                                currentStatus={displayedStatuses[item.field]}
                            />
                        )}
                    </Stack>
                    <Stack direction="row">
                        {canEdit && (
                            <TooltipIconButton
                                key="status"
                                icon={<SellIcon />}
                                onClick={() => setOpenFieldStatusModal(true)}
                                tooltip={statusToolTip}
                            />
                        )}
                        {canUpdateFieldVisibility && !isTable && (
                            <TooltipIconButton
                                key="visibility"
                                icon={visibilityButton}
                                onClick={toggleIsVisible}
                                tooltip={visibilityTooltip}
                                className={hiddenClassName}
                            />
                        )}
                    </Stack>
                </Stack>
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="caption" color="textSecondary">
                        {t(`dataCatalog.${item.type}`)}
                    </Typography>
                    {isAdminUser && mappingTarget && (
                        <Button
                            className="pendo-data-catalog-mapping-button"
                            size="small"
                            variant="text"
                            component={Link}
                            to={`${routes.mappings}?category=${mappingTarget.category}&software=${mappingTarget.software}`}
                        >
                            {t(mappingTarget.labelKey)}
                        </Button>
                    )}
                </Stack>
            </Stack>
            {canEdit && openFieldStatusModal && (
                <EditFieldStatusModal
                    fieldName={item.field}
                    setOpenFieldStatusModal={setOpenFieldStatusModal}
                    isTable={isTable}
                    fieldStatusData={fieldStatusData}
                />
            )}
        </>
    );
};

export default FieldHeader;
